import bigCircleLogo from '../assets/og-image.png';
import ForgotPassword from '../components/ForgotPassword';
import MobileSignIn from '../components/MobileSignIn';
import { useEffect, useState } from 'react';
import { get } from '../lib/api';
import { useNavigate, useLocation } from 'react-router-dom';

import {
  ArrowLeft,
  CheckCircle2,
  Eye,
  EyeOff,
  Lock,
  Mail,
  MapPin,
  Phone,
  User,
  Utensils,
  Loader2,
  ShoppingBag,
} from 'lucide-react';

import SocialSignInServices from '../components/SocialSignInServices';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { GOOGLE_CLIENT_ID } from '../lib/googleConfig';

import { useAuth } from '../context/AuthContext';
import { getAuthReturnPath } from '../lib/auth';
import { customerHomePath } from '../lib/customerHome';
import { getLastLogin, getAuthGreeting, markAuthVisit } from '../lib/lastLogin';

const Auth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const mobileAuth = !process.env.REACT_APP_PORTAL_ROLE || ['CUSTOMER', 'VENDOR'].includes(process.env.REACT_APP_PORTAL_ROLE);
  const [loginMethods, setLoginMethods] = useState({ MOBILE: true, GOOGLE: true, FACEBOOK: true });
  const [methodsLoaded, setMethodsLoaded] = useState(!mobileAuth);
  const [methodsError, setMethodsError] = useState(false);
  useEffect(() => {
    if (!mobileAuth) return;
    let active = true;
    get('/system-controls').then(r => { if (active) { setLoginMethods(r.systemControls?.loginMethods || { MOBILE: true, GOOGLE: true, FACEBOOK: true }); setMethodsLoaded(true); } }).catch(() => { if (active) setMethodsError(true); });
    return () => { active = false; };
  }, [mobileAuth]);
  const showGoogle = methodsLoaded && (!mobileAuth || loginMethods.GOOGLE);
  const showFacebook = methodsLoaded && (!mobileAuth || loginMethods.FACEBOOK);
  const returnPath = getAuthReturnPath(location.state?.from, 'CUSTOMER');
  const destinationForRole = role => getAuthReturnPath(location.state?.from, role);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [lastLogin] = useState(getLastLogin);
  const [greeting] = useState(getAuthGreeting);
  useEffect(() => { markAuthVisit(); }, []);
  const lastMethodLabel = { GOOGLE: 'Google', FACEBOOK: 'Facebook', PHONE: 'phone', EMAIL: 'email and password' }[lastLogin?.method];
  const lastMethodAvailable = lastLogin?.method === 'EMAIL' ? !mobileAuth
    : lastLogin?.method === 'PHONE' ? mobileAuth && methodsLoaded && loginMethods.MOBILE
      : lastLogin?.method === 'GOOGLE' ? showGoogle : lastLogin?.method === 'FACEBOOK' ? showFacebook : false;
  const [googleChallenge, setGoogleChallenge] = useState(null);
  const [googleCode, setGoogleCode] = useState('');

  // ==========================================================
  // MODE
  // ==========================================================

  const [isLogin, setIsLogin] = useState(true);
  const deliveryLogin = isLogin && process.env.REACT_APP_PORTAL_ROLE === 'DELIVERY';

  // ==========================================================
  // FORM
  // ==========================================================

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');

  // ==========================================================
  // UI STATE
  // ==========================================================

  // Normal email/password loading
  const [loading, setLoading] = useState(false);

  // Social login loading states are kept separate
  const [googleLoading, setGoogleLoading] = useState(false);
  const [facebookLoading, setFacebookLoading] = useState(false);

  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // ==========================================================
  // REGISTRATION SUCCESS
  // ==========================================================

  const [registrationSuccess, setRegistrationSuccess] =
    useState(false);

  const [registeredEmail, setRegisteredEmail] =
    useState('');

  // ==========================================================
  // AUTHENTICATION
  // ==========================================================

  const {
    login,
    register,
    loginWithGoogle,
    loginWithFacebook,
    confirmGooglePhone,
  } = useAuth();

  // ==========================================================
  // GLOBAL AUTH LOADING CHECK
  // ==========================================================

  const isAuthenticating =
    loading ||
    googleLoading ||
    facebookLoading;

  // ==========================================================
  // ERROR HELPER
  // ==========================================================

  const getErrorMessage = (err, fallback) => {
    const server = err?.response?.data;

    if (Array.isArray(server?.errors)) {
      return server.errors
        .map(
          (item) =>
            `${item.field}: ${item.message}`
        )
        .join('; ');
    }

    if (server?.message) {
      return server.message;
    }

    return err?.message || fallback;
  };

  // ==========================================================
  // EMAIL / PASSWORD AUTHENTICATION
  // ==========================================================

  const handleAuth = async (e) => {
    e.preventDefault();

    if (isAuthenticating) {
      return;
    }

    setError('');
    setLoading(true);

    try {
      // ========================================================
      // LOGIN
      // ========================================================

      if (isLogin) {
        const user = await login(
          email.trim(),
          password
        );

        console.log(
          'Logged in user:',
          user
        );

        navigate(
          destinationForRole(user?.role),
          {
            replace: true, state: location.state?.returnState,
          }
        );

        return;
      }

      // ========================================================
      // REGISTRATION
      // ========================================================

      const response = await register({
        name: name.trim(),
        email: email.trim(),
        password,
        phone: phone.trim(),
        role: 'CUSTOMER',
      });

      console.log(
        'Registration response:',
        response
      );

      if (response?.success === true) {
        setRegisteredEmail(
          email.trim()
        );

        navigate(returnPath, { replace: true, state: location.state?.returnState });

        // Never retain password
        setPassword('');

        return;
      }

      setError(
        response?.message ||
        'Registration failed. Please try again.'
      );

    } catch (err) {
      console.error(
        'Authentication error:',
        err
      );

      setError(
        getErrorMessage(
          err,
          'An unexpected error occurred. Please try again.'
        )
      );

    } finally {
      setLoading(false);
    }
  };

  // ==========================================================
  // GOOGLE LOGIN
  // ==========================================================
  //
  // GoogleLogin provides the Google ID token.
  //
  // AuthContext is responsible for requesting the separate
  // Google OAuth access token with:
  //
  // https://www.googleapis.com/auth/user.phonenumbers.read
  //
  // That access token is then sent to the backend so the
  // backend can call the Google People API.
  //
  // ==========================================================

  const handleGoogleSuccess = async (
    credentialResponse
  ) => {
    if (isAuthenticating) {
      return;
    }

    setError('');
    setGoogleLoading(true);

    try {
      const googleCredential =
        credentialResponse?.credential;

      if (!googleCredential) {
        throw new Error(
          'Google authentication failed. No credential was received.'
        );
      }

      console.log(
        'Google credential received.'
      );

      // AuthContext obtains the OAuth access token.
      const user =
        await loginWithGoogle({
          credential:
            googleCredential,
        });

      if (user?.verificationRequired) {
        setGoogleChallenge(user); setGoogleCode('');
        return;
      }

      console.log(
        'Google logged in user:',
        user
      );

      navigate(
        destinationForRole(user?.role),
        {
          replace: true, state: location.state?.returnState,
        }
      );

    } catch (err) {
      console.error(
        'Google login error:',
        err
      );

      setError(
        getErrorMessage(
          err,
          'Google sign-in failed. Please try again.'
        )
      );

    } finally {
      setGoogleLoading(false);
    }
  };

  // ==========================================================
  // GOOGLE ERROR
  // ==========================================================

  const handleGoogleError = () => {
    setGoogleLoading(false);

    setError(
      'Google sign-in was cancelled or failed. Please try again.'
    );
  };

  // ==========================================================
  // FACEBOOK LOGIN
  // ==========================================================

  const handleFacebookLogin = () => {
    if (isAuthenticating) {
      return;
    }

    setError('');
    setFacebookLoading(true);

    // Facebook SDK should be initialized globally.

    if (typeof window.FB?.login !== 'function') {
      setFacebookLoading(false);

      setError(
        'Facebook Login is not available right now. Please try again later.'
      );

      return;
    }

    const completeFacebookLogin = async (response) => {
        try {
          const facebookAccessToken =
            response?.authResponse
              ?.accessToken;

          if (!facebookAccessToken) {
            throw new Error(
              'Facebook sign-in was cancelled.'
            );
          }

          console.log(
            'Facebook access token received.'
          );

          const user =
            await loginWithFacebook({
              accessToken:
                facebookAccessToken,
            });

          console.log(
            'Facebook logged in user:',
            user
          );

          navigate(
            destinationForRole(user?.role),
            {
              replace: true, state: location.state?.returnState,
            }
          );

        } catch (err) {
          console.error(
            'Facebook login error:',
            err
          );

          setError(
            getErrorMessage(
              err,
              'Facebook sign-in failed. Please try again.'
            )
          );

        } finally {
          setFacebookLoading(false);
        }
    };

    try {
      // The Facebook SDK requires a regular callback, not an async function.
      window.FB.login(
        (response) => { void completeFacebookLogin(response); },
        { scope: 'public_profile,email' }
      );
    } catch (err) {
      setFacebookLoading(false);
      setError(getErrorMessage(err, 'Facebook sign-in failed. Please try again.'));
    }
  };

  // ==========================================================
  // SWITCH LOGIN / REGISTER
  // ==========================================================

  const switchMode = () => {
    if (isAuthenticating) {
      return;
    }

    setIsLogin(
      (current) => !current
    );

    setError('');
    setShowPassword(false);
  };

  // ==========================================================
  // REGISTRATION SUCCESS SCREEN
  // ==========================================================

  if (forgotPassword) return <ForgotPassword initialEmail={email} onClose={() => { setForgotPassword(false); setPassword(''); setError(''); }} />;
  if (registrationSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex">

        {/* ==================================================
            LEFT BRAND PANEL
            ================================================== */}

        <div className="hidden lg:flex lg:w-1/2 bg-primary relative overflow-hidden">

          <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-white/10" />

          <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-white/10" />

          <div className="relative z-10 flex flex-col justify-between p-12 w-full text-white">

            <button
              type="button"
              onClick={() => navigate(customerHomePath)}
              className="flex items-center gap-3 w-fit"
            >
              <img src={bigCircleLogo} alt="BigCircle" className="h-28 w-auto object-contain sm:h-32" />
            </button>

            <div className="max-w-md">

              <div className="w-16 h-16 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center mb-6">
                <CheckCircle2 className="w-9 h-9" />
              </div>

              <h1 className="text-4xl font-bold leading-tight">
                You're almost there.
              </h1>

              <p className="mt-4 text-white/80 text-lg leading-relaxed">
                Verify your email and start discovering
                delicious food from your favorite local
                vendors.
              </p>

            </div>

            <p className="text-sm text-white/60">
              © 2026 BigCircle PH. All rights reserved.
            </p>

          </div>
        </div>

        {/* ==================================================
            SUCCESS CONTENT
            ================================================== */}

        <div className="flex-1 flex items-center justify-center p-6">

          <div className="w-full max-w-md">

            {/* Mobile logo */}

            <div className="flex lg:hidden items-center justify-center gap-2 mb-10">

              <img src={bigCircleLogo} alt="BigCircle" className="h-28 w-auto object-contain sm:h-32" />

            </div>

            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 sm:p-10">

              <div className="flex justify-center">

                <div className="w-20 h-20 rounded-full bg-green-50 flex items-center justify-center">
                  <Mail className="w-9 h-9 text-green-600" />
                </div>

              </div>

              <div className="text-center mt-6">

                <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
                  Account created
                </h2>

                <p className="mt-3 text-gray-500 leading-relaxed">
                  Your account has been created successfully.
                </p>

                <p className="mt-5 text-sm text-gray-500">
                  Your registered email
                </p>

                <p className="mt-1 font-semibold text-primary break-all">
                  {registeredEmail}
                </p>

              </div>

              <div className="mt-6 rounded-2xl bg-primary/5 border border-primary/10 p-4">

                <div className="flex gap-3">

                  <Mail className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />

                  <p className="text-sm text-gray-600 leading-relaxed">
                    Your account is pending verification. Continue to verify
                    using the method selected by the platform.
                  </p>

                </div>

              </div>

              <p className="mt-5 text-center text-xs text-gray-400">
                Complete verification from your customer profile.
              </p>

              <button
                type="button"
                onClick={() => {
                  setRegistrationSuccess(false);
                  setIsLogin(true);
                  setEmail(registeredEmail);
                  setError('');
                  setShowPassword(false);
                }}
                className="
                  mt-7
                  w-full
                  h-12
                  rounded-xl
                  bg-primary
                  text-white
                  font-semibold
                  hover:bg-primary/90
                  active:scale-[0.99]
                  transition
                  flex
                  items-center
                  justify-center
                  gap-2
                "
              >
                Continue to Sign In
              </button>

              <button
                type="button"
                onClick={() =>
                  navigate(customerHomePath)
                }
                className="
                  mt-4
                  w-full
                  text-sm
                  text-gray-500
                  hover:text-gray-900
                  transition
                  flex
                  items-center
                  justify-center
                  gap-2
                "
              >
                <ArrowLeft className="w-4 h-4" />
                Back to BigCircle
              </button>

            </div>

          </div>

        </div>

      </div>
    );
  }

  // ==========================================================
  // LOGIN / REGISTRATION PAGE
  // ==========================================================

  return (
    <div className="min-h-screen bg-gray-50 flex">

      {/* ==================================================
          LEFT BRANDING PANEL
          ================================================== */}

      <div className="hidden lg:flex lg:w-1/2 bg-primary relative overflow-hidden">

        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-white/10" />

        <div className="absolute top-1/3 -right-48 w-[550px] h-[550px] rounded-full bg-white/10" />

        <div className="absolute -bottom-48 left-1/4 w-[450px] h-[450px] rounded-full bg-white/5" />

        <div className="relative z-10 flex flex-col justify-between p-12 w-full text-white">

          {/* Logo */}

          <button
            type="button"
            onClick={() => navigate(customerHomePath)}
            className="flex items-center gap-3 w-fit"
          >
            <img src={bigCircleLogo} alt="BigCircle" className="h-28 w-auto object-contain sm:h-32" />
          </button>

          {/* Hero */}

          <div className="max-w-lg">

            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/10 px-4 py-2 text-sm mb-6">

              <Utensils className="w-4 h-4" />

              <span>
                Your local food marketplace
              </span>

            </div>

            <h1 className="text-5xl xl:text-6xl font-bold leading-[1.05]">
              Great food,
              <br />
              delivered to you.
            </h1>

            <p className="mt-6 text-lg text-white/75 max-w-md leading-relaxed">
              Discover restaurants, explore delicious
              dishes, and have your favorites delivered
              right to your doorstep.
            </p>

            <div className="mt-10 space-y-4">

              <div className="flex items-center gap-3">

                <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
                  <MapPin className="w-4 h-4" />
                </div>

                <span className="text-white/85">
                  Discover food near you
                </span>

              </div>

              <div className="flex items-center gap-3">

                <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
                  <ShoppingBag className="w-4 h-4" />
                </div>

                <span className="text-white/85">
                  Order from local vendors
                </span>

              </div>

              <div className="flex items-center gap-3">

                <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
                  <CheckCircle2 className="w-4 h-4" />
                </div>

                <span className="text-white/85">
                  Fast and convenient delivery
                </span>

              </div>

            </div>

          </div>

          <p className="text-sm text-white/50">
            © 2026 BigCircle. All rights reserved.
          </p>

        </div>
      </div>

      {/* ==================================================
          RIGHT AUTH PANEL
          ================================================== */}

      <div className="flex-1 flex items-center justify-center p-5 sm:p-8">

        <div className="w-full max-w-md">

          {/* Back to home */}

          <button
            type="button"
            onClick={() => navigate(customerHomePath)}
            disabled={isAuthenticating}
            className="
              mb-6
              text-sm
              text-gray-500
              hover:text-gray-900
              transition
              flex
              items-center
              gap-2
              disabled:opacity-50
            "
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </button>

          {/* Auth Card */}

          <div className="bg-white border border-gray-100 rounded-3xl shadow-xl p-7 sm:p-9">

            {/* Header */}

            <div className="mb-8">

              <div className="mb-6 flex justify-center lg:hidden">
                <img src={bigCircleLogo} alt="BigCircle" className="h-20 w-auto object-contain sm:h-24" />
              </div>

              <div className="hidden lg:flex w-12 h-12 rounded-2xl bg-primary/10 text-primary items-center justify-center mb-5">

                {isLogin ? (
                  <User className="w-6 h-6" />
                ) : (
                  <ShoppingBag className="w-6 h-6" />
                )}

              </div>
              <h2 className="break-words text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">

                {isLogin
                  ? greeting
                  : 'Create your account'}

              </h2>

              <p className="mt-2 text-gray-500">

                {isLogin
                  ? 'Sign in to continue to BigCircle.'
                  : 'Create an account, then complete verification to start ordering.'}

              </p>

              {isLogin && lastMethodLabel && methodsLoaded && <div role="status" className="mt-4 flex items-start gap-2 rounded-2xl border border-green-100 bg-green-50 px-4 py-3 text-sm text-green-900">
                <CheckCircle2 aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{lastMethodAvailable ? `Last time you signed in with ${lastMethodLabel}. Continue with the same method for your account.` : `You last signed in with ${lastMethodLabel}. That method is currently unavailable; choose another available method or contact support.`}</span>
              </div>}

            </div>

            {/* Error */}

            {googleChallenge && <form className="mb-6 space-y-3" onSubmit={async event => {
              event.preventDefault(); setGoogleLoading(true); setError('');
              try {
                const user = await confirmGooglePhone(googleChallenge.challengeId, googleCode);
                navigate(destinationForRole(user?.role), { replace: true, state: location.state?.returnState });
              } catch (err) { setError(getErrorMessage(err, 'Unable to verify the code.')); }
              finally { setGoogleLoading(false); }
            }}>
              <p role="status" className="text-sm text-gray-600">To continue with Google for your phone account, enter the SMS code sent to {googleChallenge.maskedDestination}. The code expires in 5 minutes.</p>
              <label className="block text-sm font-medium">SMS verification code
                <input required autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]{6}" maxLength={6}
                  value={googleCode} disabled={isAuthenticating} onChange={event => setGoogleCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="mt-2 w-full rounded-xl border p-3" />
              </label>
              <button type="submit" disabled={isAuthenticating || googleCode.length !== 6} className="w-full rounded-xl bg-primary p-3 text-white disabled:opacity-60">Verify and continue with Google</button>
              <button type="button" disabled={isAuthenticating} onClick={() => { setGoogleChallenge(null); setGoogleCode(''); setError(''); }} className="text-sm text-primary">Cancel / request another code with Google</button>
            </form>}

            {error && (
              <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3">

                <p className="text-sm text-red-700 leading-relaxed">
                  {error}
                </p>

              </div>
            )}

            {/* ==================================================
                SOCIAL LOGIN
                ================================================== */}

            {(showGoogle || showFacebook) && (
              <div className="mb-7">

                <div className="space-y-3">

                  {/* GOOGLE */}

                  {showGoogle && <div className="w-full relative">

                    {lastLogin?.method === 'GOOGLE' && <p className="mb-1 text-xs font-semibold text-primary">Last used</p>}

                    {googleLoading && (
                      <div className="
                        absolute
                        inset-0
                        z-10
                        rounded-xl
                        bg-white/80
                        flex
                        items-center
                        justify-center
                        gap-2
                        text-sm
                        text-gray-600
                      ">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>
                          Connecting to Google...
                        </span>
                      </div>
                    )}

                    <div
                      className={
                        isAuthenticating
                          ? 'opacity-60 pointer-events-none'
                          : ''
                      }
                    >
                      {GOOGLE_CLIENT_ID ? <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}><GoogleLogin
                        onSuccess={
                          handleGoogleSuccess
                        }
                        onError={
                          handleGoogleError
                        }
                        useOneTap={false}
                        theme="outline"
                        size="large"
                        text="continue_with"
                        shape="rectangular"
                        width="320"
                      /></GoogleOAuthProvider> : <p className="text-sm text-gray-600" role="status">
                        Google sign-in is currently unavailable. Please use email or phone.
                      </p>}
                    </div>

                  </div>}

                  {/* FACEBOOK */}
                  {showFacebook && <><SocialSignInServices />

                  {lastLogin?.method === 'FACEBOOK' && <p className="text-xs font-semibold text-primary">Last used</p>}

                  <button
                    type="button"
                    onClick={
                      handleFacebookLogin
                    }
                    disabled={
                      facebookLoading ||
                      googleLoading ||
                      loading
                    }
                    className="
                      w-full
                      h-12
                      rounded-xl
                      border
                      border-gray-200
                      bg-white
                      text-gray-700
                      font-medium
                      hover:bg-gray-50
                      hover:border-gray-300
                      active:scale-[0.99]
                      transition
                      disabled:opacity-60
                      disabled:cursor-not-allowed
                      flex
                      items-center
                      justify-center
                      gap-3
                    "
                  >

                    {facebookLoading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path
                          fill="#1877F2"
                          d="M24 12a12 12 0 1 0-13.88 11.85v-8.39H7.08V12h3.04V9.41c0-3 1.79-4.66 4.52-4.66 1.31 0 2.68.23 2.68.23v2.95h-1.51c-1.49 0-1.95.92-1.95 1.87V12h3.32l-.53 3.46h-2.79v8.39A12 12 0 0 0 24 12Z"
                        />
                      </svg>
                    )}

                    <span>
                      {facebookLoading
                        ? 'Connecting to Facebook...'
                        : 'Continue with Facebook'}
                    </span>

                  </button></>}

                </div>

                {/* Divider */}

                {(!mobileAuth || loginMethods.MOBILE) && <div className="relative my-6">

                  <div className="absolute inset-0 flex items-center">

                    <div className="w-full border-t border-gray-200" />

                  </div>

                  <div className="relative flex justify-center">

                    <span className="px-4 bg-white text-xs text-gray-400 uppercase tracking-wider">
                      {mobileAuth ? 'Or continue with mobile number' : 'Or continue with email'}
                    </span>

                  </div>

                </div>}

              </div>
            )}

            {/* ==================================================
                EMAIL / PASSWORD FORM
                ================================================== */}

            {mobileAuth && !methodsLoaded && <p role="status">{methodsError ? "Unable to load login options. Please reload to try again." : "Loading login options..."}</p>}
            {mobileAuth ? (methodsLoaded && loginMethods.MOBILE && <MobileSignIn
              disabled={googleLoading || facebookLoading}
              onBusyChange={setLoading}
              onSignedIn={user => navigate(destinationForRole(user?.role), { replace: true, state: location.state?.returnState })}
            />) : <><form
              onSubmit={handleAuth}
              className="space-y-5"
            >

              {/* NAME */}

              {!isLogin && (
                <div>

                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Full name
                  </label>

                  <div className="relative">

                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />

                    <input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) =>
                        setName(e.target.value)
                      }
                      placeholder="Enter your full name"
                      autoComplete="name"
                      disabled={isAuthenticating}
                      required
                      className="
                        w-full
                        h-12
                        rounded-xl
                        border
                        border-gray-200
                        bg-gray-50
                        pl-12
                        pr-4
                        text-sm
                        text-gray-900
                        placeholder:text-gray-400
                        outline-none
                        transition
                        focus:bg-white
                        focus:border-primary
                        focus:ring-4
                        focus:ring-primary/10
                      "
                    />

                  </div>

                </div>
              )}

              {/* PHONE */}

              {!isLogin && (
                <div>

                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Phone number
                  </label>

                  <div className="relative">

                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />

                    <input
                      id="phone"
                      type="tel"
                      value={phone}
                      onChange={(e) =>
                        setPhone(e.target.value)
                      }
                      placeholder="Enter your phone number"
                      autoComplete="tel"
                      disabled={isAuthenticating}
                      required
                      className="
                        w-full
                        h-12
                        rounded-xl
                        border
                        border-gray-200
                        bg-gray-50
                        pl-12
                        pr-4
                        text-sm
                        text-gray-900
                        placeholder:text-gray-400
                        outline-none
                        transition
                        focus:bg-white
                        focus:border-primary
                        focus:ring-4
                        focus:ring-primary/10
                      "
                    />

                  </div>

                </div>
              )}

              {/* EMAIL */}

              <div>

                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {deliveryLogin ? 'Email or mobile number' : 'Email address'}
                </label>

                <div className="relative">

                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />

                  <input
                    id="email"
                    type={deliveryLogin ? 'text' : 'email'}
                    value={email}
                    onChange={(e) =>
                      setEmail(e.target.value)
                    }
                    placeholder={deliveryLogin ? 'you@example.com or 09171234567' : 'you@example.com'}
                    autoComplete={deliveryLogin ? 'username' : 'email'}
                    disabled={isAuthenticating}
                    required
                    className="
                      w-full
                      h-12
                      rounded-xl
                      border
                      border-gray-200
                      bg-gray-50
                      pl-12
                      pr-4
                      text-sm
                      text-gray-900
                      placeholder:text-gray-400
                      outline-none
                      transition
                      focus:bg-white
                      focus:border-primary
                      focus:ring-4
                      focus:ring-primary/10
                    "
                  />

                </div>

              </div>

              {/* PASSWORD */}

              <div>

                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Password
                </label>

                <div className="relative">

                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />

                  <input
                    id="password"
                    type={
                      showPassword
                        ? 'text'
                        : 'password'
                    }
                    value={password}
                    onChange={(e) =>
                      setPassword(e.target.value)
                    }
                    placeholder="Enter your password"
                    autoComplete={
                      isLogin
                        ? 'current-password'
                        : 'new-password'
                    }
                    disabled={isAuthenticating}
                    required
                    className="
                      w-full
                      h-12
                      rounded-xl
                      border
                      border-gray-200
                      bg-gray-50
                      pl-12
                      pr-12
                      text-sm
                      text-gray-900
                      placeholder:text-gray-400
                      outline-none
                      transition
                      focus:bg-white
                      focus:border-primary
                      focus:ring-4
                      focus:ring-primary/10
                    "
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowPassword(
                        (current) =>
                          !current
                      )
                    }
                    disabled={isAuthenticating}
                    className="
                      absolute
                      right-3
                      top-1/2
                      -translate-y-1/2
                      w-9
                      h-9
                      rounded-lg
                      flex
                      items-center
                      justify-center
                      text-gray-400
                      hover:text-gray-700
                      hover:bg-gray-100
                      transition
                    "
                    aria-label={
                      showPassword
                        ? 'Hide password'
                        : 'Show password'
                    }
                  >

                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}

                  </button>

                </div>

              </div>

              {isLogin && <button type="button" disabled={isAuthenticating} onClick={() => setForgotPassword(true)} className="block ml-auto text-sm font-semibold text-primary hover:underline">Forgot password?</button>}
              {/* SUBMIT */}
          {/* Bottom text */}

          <p className="mt-7 text-center text-xs text-gray-400">

            By signing in, you agree to BigCircle's{' '}

            <button
              type="button"
              onClick={() =>
                navigate('/legals')
              }
              className="text-primary hover:underline"
            >
              Terms of Service and Privacy Policy.
            </button>

          </p>
              <button
                type="submit"
                disabled={isAuthenticating}
                className="
                  w-full
                  h-12
                  rounded-xl
                  bg-primary
                  text-white
                  font-semibold
                  shadow-sm
                  hover:bg-primary/90
                  hover:shadow-md
                  active:scale-[0.99]
                  transition
                  disabled:opacity-60
                  disabled:cursor-not-allowed
                  flex
                  items-center
                  justify-center
                  gap-2
                "
              >

                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />

                    <span>
                      {isLogin
                        ? 'Signing in...'
                        : 'Creating account...'}
                    </span>
                  </>
                ) : (
                  <span>
                    {isLogin
                      ? 'Sign In'
                      : 'Create Account'}
                  </span>
                )}

              </button>

            </form>

            {/* SWITCH MODE */}

            <div className="mt-7 pt-6 border-t border-gray-100 text-center">

              <p className="text-sm text-gray-500">

                {isLogin
                  ? "Don't have an account?"
                  : 'Already have an account?'}

              </p>

              <button
                type="button"
                onClick={switchMode}
                disabled={isAuthenticating}
                className="
                  mt-1
                  text-sm
                  font-semibold
                  text-primary
                  hover:underline
                  disabled:opacity-50
                "
              >

                {isLogin
                  ? 'Create an account'
                  : 'Sign in instead'}

              </button>

            </div></>}

          </div>

        </div>

      </div>

    </div>
  );
};

export default Auth;
